<template>
  <modal v-model="model" max-width="960">
    <template #title>PRISMA Statistics</template>
    <template #content>
      <v-container class="border rounded">
        <v-row
          v-for="(category, categoryIdx) in categories"
          :key="categoryIdx"
          class="mx-4"
        >
          <v-col>
            <v-row
              class="text-h6 grey--text text--darken-2 font-weight-bold"
            >
              {{ category.title }}
            </v-row>
            <v-row>
              <v-col
                v-for="(items, columnIdx) in category.columns"
                :key="columnIdx"
                class="text-subtitle-1"
                :md="columnIdx == 0 ? 5 : 7"
              >
                <template v-for="(item, itemIdx) in items">
                  <v-row :key="`item:${itemIdx}`">
                    <prisma-stat
                      :icon-color="item.iconColor"
                      :icon="item.icon"
                      :stat-label="item.statLabel"
                      :count="item.count"
                    />
                  </v-row>
                  <div
                    v-if="item.subItems"
                    :key="`subitems:${itemIdx}`"
                  >
                    <v-row
                      v-for="(subItem, subItemIdx) in item.subItems"
                      :key="`subitem:${itemIdx}:${subItemIdx}`"
                      class="ml-2"
                    >
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on">
                            <prisma-stat
                              :icon-color="subItem.iconColor"
                              :icon="subItem.icon"
                              :stat-label="subItem.statLabel"
                              :count="subItem.count"
                            />
                          </span>
                        </template>
                        <span>
                          {{
                            `${subItem.count} ${subItem.statLabel}`
                          }}
                        </span>
                      </v-tooltip>
                    </v-row>
                  </div>
                </template>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <template #actions>
      <action-button class="mr-3" @click="model = false">
        Close
      </action-button>
    </template>
  </modal>
</template>

<script>
import ActionButton from '@/components/Common/ActionButton';
import Modal from '@/components/Common/Modal';
import PrismaStat from './PrismaStat';
import { PrismaStats } from '@/models';
import { GetReviewPrismaStats } from '@/graphql';

export default {
  name: 'PrismaStatsDialog',
  components: {
    ActionButton,
    Modal,
    PrismaStat,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // Either pass pre-computed stats (e.g. a revision's snapshot) or a
    // reviewId for the dialog to fetch the review's current stats itself.
    reviewId: {
      type: String,
      default: null,
    },
    prismaStats: {
      type: PrismaStats,
      default: null,
    },
  },
  emits: ['input'],
  data: () => ({
    reviewData: null,
  }),
  apollo: {
    reviewData: {
      query: GetReviewPrismaStats,
      variables() {
        return {
          reviewId: this.reviewId,
        };
      },
      skip() {
        // Only fetch when the dialog is open and stats weren't passed in
        return !this.value || !!this.prismaStats || !this.reviewId;
      },
      update: (data) => data.review,
    },
  },
  computed: {
    stats() {
      if (this.prismaStats) {
        return this.prismaStats;
      }
      // Return the fetched prismaStats or a default empty object
      if (this.reviewData && this.reviewData.prismaStats) {
        return new PrismaStats(this.reviewData.prismaStats);
      }
      // Return default empty stats while loading
      return new PrismaStats({});
    },
    model: {
      get: function () {
        return this.value;
      },
      set: function (value) {
        this.$emit('input', value);
      },
    },
    categories() {
      return [
        {
          title: 'Identification',
          columns: [
            [
              {
                iconColor: 'primary',
                icon: 'storage',
                statLabel: 'records from database',
                count: this.stats.recordsFromDatabase,
              },
              {
                iconColor: 'primary',
                icon: 'assignment',
                statLabel: 'records from registry',
                count: this.stats.recordsFromRegistry,
              },
              {
                iconColor: 'primary',
                icon: 'folder',
                statLabel: 'records from other',
                count: this.stats.recordsFromOther,
              },
              {
                iconColor: 'primary',
                icon: 'undo',
                statLabel: 'records from previous revisions',
                count: this.stats.recordsFromPriorRevisions,
              },
            ],
            [
              {
                iconColor: 'error',
                icon: 'copy',
                statLabel: 'duplicate records',
                count: this.stats.duplicateRecords,
              },
              {
                iconColor: 'error',
                icon: 'do_disturb',
                statLabel: 'ineligible records',
                count: this.stats.ineligibleRecords,
              },
            ],
          ],
        },
        {
          title: 'Screening',
          columns: [
            [
              {
                iconColor: 'primary',
                icon: 'dvr',
                statLabel: 'records screened',
                count: this.stats.recordsScreened,
              },
            ],
            [
              {
                iconColor: 'error',
                icon: 'do_disturb',
                statLabel: 'records excluded by reviewers',
                count: this.stats.recordsExcludedByReviewers,
              },
              {
                iconColor: 'error',
                icon: 'do_disturb',
                statLabel: 'records excluded by system',
                count: this.stats.recordsExcludedBySystem,
              },
            ],
          ],
        },
        {
          title: 'Report Retrieval',
          columns: [
            [
              {
                iconColor: 'primary',
                icon: 'dvr',
                statLabel: 'reports retrieved',
                count: this.stats.reportsRetrieved,
              },
            ],
            [
              {
                iconColor: 'error',
                icon: 'do_disturb',
                statLabel: 'reports not retrieved',
                count: this.stats.reportsNotRetrieved,
              },
            ],
          ],
        },
        {
          title: 'Eligibility Assessment',
          columns: [
            [
              {
                iconColor: 'primary',
                icon: 'dvr',
                statLabel: 'reports assessed for eligibility',
                count: this.stats.reportsReviewed,
              },
            ],
            [
              {
                iconColor: 'error',
                icon: 'do_disturb',
                statLabel: 'reports excluded',
                count: this.stats.reportsExcluded,
                subItems: (
                  this.stats.reportsExcludedByReason || []
                ).map((reason) => ({
                  iconColor: 'error',
                  icon: 'subject',
                  statLabel: reason.exclusionCriteria.description,
                  count: reason.count,
                })),
              },
            ],
          ],
        },
        {
          title: 'Inclusion',
          columns: [
            [
              {
                iconColor: 'success',
                icon: 'dvr',
                statLabel: 'reports of included studies',
                count: this.stats.reportsIncluded,
              },
            ],
          ],
        },
        {
          title: 'Ongoing',
          columns: [
            [
              {
                iconColor: 'primary',
                icon: 'dvr',
                statLabel: 'studies awaiting classification',
                count: this.stats.studiesAwaitingClassification,
              },
              {
                iconColor: 'warning',
                icon: 'dvr',
                statLabel: 'included studies ongoing',
                count: this.stats.includedStudiesOngoing,
              },
            ],
          ],
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-body {
  line-height: 150% !important;
}
.border {
  border: 1px solid;
  border-color: var(--v-primary-base);
}
</style>
